import { questionsCacheKey, seriesCacheKey } from '@mentimeter/core-hooks';
import { MentiError } from '@mentimeter/error-utils/error';
import {
  Channels,
  SeriesPrivateEvents,
  usePublish,
} from '@mentimeter/realtime';
import { captureException } from '@mentimeter/errors/sentry';
import { useCallback } from 'react';
import { type MutationFetcher } from 'swr/mutation';

/**
 * Pragmatic and simplified inline copy of `getEditorSWRCacheKey` from
 * `@mentimeter/editor-data-hooks` to avoid circular dependencies.
 */
function getEditorSWRCacheKey(
  key: 'slideDeck' | 'slides',
  { slideDeckPublicKey }: { slideDeckPublicKey: string },
) {
  const DOMAIN = 'editor';
  const SLIDE_DECK = 'slide-deck';

  switch (key) {
    case 'slideDeck':
      // TODO: Remove the `core-hooks` cache key.
      return seriesCacheKey(slideDeckPublicKey);
      return [DOMAIN, SLIDE_DECK, slideDeckPublicKey].join('/');
    case 'slides':
      // TODO: Remove the `core-hooks` cache key.
      return questionsCacheKey(slideDeckPublicKey);
      return [DOMAIN, SLIDE_DECK, slideDeckPublicKey, 'slides'].join('/');
    default:
      throw new Error(`Invalid cache key: ${key}`);
  }
}

export const useEditorSlideDeckUpdateNotifier = <
  Result = unknown,
  Args = unknown,
>(
  slideDeckOrSeriesId: string,
  makeRequest: MutationFetcher<Result, string, Args>,
): MutationFetcher<Result, string, Args> => {
  const publish = usePublish({
    channel: Channels.SERIES_PRIVATE,
    value: slideDeckOrSeriesId,
  });

  const wrapper = useCallback<MutationFetcher<Result, string, Args>>(
    async (key: string, { arg }) => {
      const result = await makeRequest(key, { arg });
      try {
        publish(SeriesPrivateEvents.UPDATE_NETWORK_CACHE, {
          cacheKeys: [
            getEditorSWRCacheKey('slideDeck', {
              slideDeckPublicKey: slideDeckOrSeriesId,
            }),
            getEditorSWRCacheKey('slides', {
              slideDeckPublicKey: slideDeckOrSeriesId,
            }),
          ],
        });
      } catch (error) {
        captureException(
          new MentiError(
            'Error occurred while sending UPDATE_NETWORK_CACHE to Ably',
            {
              cause: error,
              feature: 'creation-experience',
            },
          ),
        );
      }
      return result;
    },
    [makeRequest, publish, slideDeckOrSeriesId],
  );

  return wrapper;
};
